/**
 * Rapports : CA, encaissé, impayés. Période = date de pièce / paiement.
 */
import { useEffect, useMemo, useState } from "react";
import { facturxFetch } from "../api/client";
import { Button } from "../components/Button";
import { PageHeader } from "../components/PageHeader";
import "../features/dashboard/dashboard.css";
import { monthStartIso, todayIso } from "../features/dashboard/period";
import { csvEuro, downloadCsv } from "../features/reports/csv";
import { PeriodBar } from "../features/reports/PeriodBar";
import { PiecesTable } from "../features/reports/PiecesTable";
import { ReportKpis } from "../features/reports/ReportKpis";
import "../features/reports/reports.css";
import { UnpaidTable } from "../features/reports/UnpaidTable";
import type { ReportPayload, ReportPiece } from "../features/reports/types";

type ReportsPageProps = {
  companyId: number;
  onOpenInvoice?: (id: number) => void;
  onOpenCredit?: (id: number) => void;
  onOpenClient?: (clientId: number) => void;
};

export function ReportsPage({ companyId, onOpenInvoice, onOpenCredit, onOpenClient }: ReportsPageProps) {
  const [mode, setMode] = useState<"month" | "custom">("month");
  const [from, setFrom] = useState(monthStartIso);
  const [to, setTo] = useState(todayIso);
  const [data, setData] = useState<ReportPayload | null>(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    const a = mode === "month" ? monthStartIso() : from || monthStartIso();
    const b = mode === "month" ? todayIso() : to || todayIso();
    return a <= b ? { from: a, to: b } : { from: b, to: a };
  }, [mode, from, to]);

  useEffect(() => {
    let stop = false;
    setLoading(true);
    facturxFetch<ReportPayload>(`reports.php?company_id=${companyId}&from=${range.from}&to=${range.to}`)
      .then((payload) => {
        if (!stop) {
          setData(payload);
          setErr("");
        }
      })
      .catch((e) => {
        if (!stop) {
          setData(null);
          setErr(e instanceof Error ? e.message : "Impossible de charger les rapports.");
        }
      })
      .finally(() => {
        if (!stop) setLoading(false);
      });
    return () => {
      stop = true;
    };
  }, [companyId, range.from, range.to]);

  function openPiece(row: ReportPiece) {
    if (row.doc_type === "credit_note") onOpenCredit?.(row.id);
    else onOpenInvoice?.(row.id);
  }

  function exportCa() {
    if (!data) return;
    const rows: (string | number)[][] = [
      ["Type", "N°", "Client", "Date", "HT", "TTC"],
      ...data.pieces.map((p) => [
        p.doc_type === "credit_note" ? "Avoir" : "Facture",
        p.number,
        p.client_name,
        p.issue_date,
        csvEuro(p.total_ht_cents),
        csvEuro(p.total_ttc_cents),
      ]),
      [],
      ["CA HT net", csvEuro(data.kpis.ca_ht_cents)],
      ["CA TTC net", csvEuro(data.kpis.ca_ttc_cents)],
      ["Encaissé TTC", csvEuro(data.kpis.collected_ttc_cents)],
    ];
    downloadCsv(`facturx-ca-${data.from}-${data.to}.csv`, rows);
  }

  function exportUnpaid() {
    if (!data) return;
    const rows: (string | number)[][] = [
      ["N°", "Client", "Échéance", "Jours de retard", "Reste TTC"],
      ...data.unpaid.map((p) => [p.number, p.client_name, p.due_date, p.days_late, csvEuro(p.remaining_cents)]),
    ];
    downloadCsv(`facturx-impayes-${data.today ?? todayIso()}.csv`, rows);
  }

  return (
    <div className="fx-report">
      <PageHeader
        title="Rapports"
        action={
          <div className="fx-report-toolbar">
            <PeriodBar mode={mode} from={from} to={to} onMode={setMode} onFrom={setFrom} onTo={setTo} />
            <div className="fx-report-actions">
              <Button variant="ghost" disabled={!data} onClick={exportCa}>
                CSV CA
              </Button>
              <Button variant="ghost" disabled={!data} onClick={exportUnpaid}>
                CSV impayés
              </Button>
            </div>
          </div>
        }
      />
      {err ? <p className="fx-dash-err">{err}</p> : null}
      {loading ? <p className="fx-demo-banner">Chargement…</p> : null}
      {data ? (
        <>
          <ReportKpis kpis={data.kpis} />
          <section>
            <h2>Pièces de la période</h2>
            <PiecesTable rows={data.pieces} onOpen={openPiece} onOpenClient={onOpenClient} />
          </section>
          <section>
            <h2>Impayés</h2>
            <UnpaidTable rows={data.unpaid} onOpenInvoice={onOpenInvoice} onOpenClient={onOpenClient} />
          </section>
        </>
      ) : null}
    </div>
  );
}
